import classNames from 'classnames';

import Divider from '@classify/react-core-components/Divider';

import { makeStyles } from '@mui/styles';

import Typography from '@classify/react-core-components/Typography';
import { alpha } from '@classify/react-core-components/styles';

import { Card } from '../../../cards';
import PathPromoCardSkeleton from './PathPromoCardSkeleton';

/* istanbul ignore next */
const useStyles = makeStyles(
  ({ spacing, palette, breakpoints, shadows }) => ({
    root: {
      [breakpoints.up('md')]: {
        minWidth: 300,
        maxWidth: 368,
      },
    },
    cardContent: {
      width: '100%',
      padding: '0 !important',
      boxShadow: shadows[2],
    },
    cardContentWithBorder: {
      borderLeftWidth: 8,
      borderLeftStyle: 'solid',
      [breakpoints.up('sm')]: {
        border: 'none !important',
      },
    },
    illustrationTopContent: {
      display: 'flex',
      flexDirection: 'column',
      [breakpoints.up('sm')]: {
        flexDirection: 'row',
      },
      [breakpoints.up('md')]: {
        flexDirection: 'column',
      },
    },
    illustrationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      width: '100%',
      minHeight: 125,
      [breakpoints.between('newsxs', 'md')]: {
        width: 206,
      },
      [breakpoints.up('sm')]: {
        minHeight: 140,
      },
      [breakpoints.up('md')]: {
        minHeight: 171,
      },
    },
    illustrationContainerCompact: {
      display: 'none',
      [breakpoints.up('sm')]: {
        display: 'flex',
      },
    },
    illustration: {
      height: '100%',
      width: 'auto',
      maxWidth: '100%',
      maxHeight: 125,
      [breakpoints.up('sm')]: {
        maxHeight: 140,
      },
      [breakpoints.up('md')]: {
        maxHeight: 171,
      },
    },
    content: {
      padding: spacing(2),
    },
    overline: {
      color: palette.primary.main,
    },
    title: {
      marginBottom: spacing(2),
    },
    metadata: {
      display: 'inline-block',
      marginBottom: spacing(1),
      [breakpoints.up('md')]: {
        display: 'block',
      },
      [breakpoints.down('sm')]: {
        display: 'block',
      },
    },
  }),
  { classNamePrefix: 'PathPromoCard' }
);

export default function PathPromoCard({
  path = null,
  metas = null,
  sections = [],
  actions = null,
  isCompactOnMobile = false,
  className = '',
  isLoading = false,
}) {
  const classes = useStyles();
  const categoryColor = path?.category.color;

  if (!path || isLoading) {
    return (
      <PathPromoCardSkeleton
        metas={metas}
        sections={sections}
        actions={actions}
        isCompactOnMobile={isCompactOnMobile}
      />
    );
  }

  return (
    <Card
      classes={{
        root: classes.root,
        content: classes.cardContent,
      }}
      className={className}
    >
      <div
        className={classNames({ [classes.cardContentWithBorder]: isCompactOnMobile })}
        style={{ borderColor: alpha(`${categoryColor}`, 0.32) }}
      >
        <div className={classes.illustrationTopContent}>
          <div
            className={classNames(classes.illustrationContainer, {
              [classes.illustrationContainerCompact]: isCompactOnMobile,
            })}
            style={{ background: alpha(`${categoryColor}`, 0.32) }}
            data-testid="PathPromoCardIllustration"
          >
            <img className={classes.illustration} src={path.illustration} alt="" />
          </div>
          <div className={classes.content}>
            <Typography
              variant="overline"
              className={classes.overline}
              style={{ color: categoryColor }}
            >
              {path.category.name}
            </Typography>
            <Typography variant="h5" className={classes.title} component="h2">
              <span>{path.title}</span>
            </Typography>

            {metas}
          </div>
        </div>
        {sections.map((section) => (
          <div key={section.key}>
            <Divider className={classes.divider} />
            <div className={classes.content}>{section.content}</div>
          </div>
        ))}
        {actions && (
          <>
            <Divider className={classes.divider} />
            {actions}
          </>
        )}
      </div>
    </Card>
  );
}
